import { Box, Button, Grid2, Stack, Tooltip, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useUnit } from 'effector-react';
import {
  $assessmentInfo,
  $currentQuestion,
  $insertSymbols,
  $insertCount,
  $questionIndex,
  $questions,
  resetAssessment,
  setAssessmentStatus,
  setInsertSymbols,
  setInsertCount,
  resetAnswerMetadata,
} from 'store/assessment/model';
import {
  ClassicEditor,
  Form,
  TextEditorControl,
  TextFieldControl,
  useToast,
} from '@myhrbox/myhrbox-frontend-core';
import { useForm } from 'react-hook-form';
import {
  Question_Correct_Count_Type,
  Question_Type,
  SaveAnswerInput,
  Trial_Question_Status,
  Trial_Status,
  useDelayQuestionMutation,
  useSaveAnswerMutation,
  useSkipQuestionMutation,
} from '@generated/graphql';
import { ClipboardEvent, useEffect, useState } from 'react';
import {
  $questionTimer,
  resetQuestionTimer,
  startQuestionTimer,
} from 'store/assessment/questionTimer';
import { useNavigate } from 'react-router-dom';
import ROUTES from 'constants/routes';
import { useResultModal } from 'context/resultModalContext';
import usePreventCopy from 'hooks/usePreventCopy';
import TextFormatter from 'components/TextFormatter';
import { useDialog } from 'context/dialogContext';
import useVoiceRecorded, { RecordStatus } from 'hooks/useVoiceRecorded';
import { secondsToTimeStringFormat } from 'utils/secondsToMMSSFormat';
import { RefetchTrialInfo } from 'store/assessment/types';
import Instruction from './components/VoiceRecordInstruction';
import VoiceRecordButton from './components/VoiceRecordButton';
import '@myhrbox/myhrbox-frontend-core/dist/myhrbox-frontend-core.css';
import MultiChoiceAnswer from './components/MultiChoiceAnswer';
import SingleChoiceAnswer from './components/SingleChoiceAnswer';
import Timer from './components/Timer';

const getQuestionTimerColor = (maxSeconds: number, seconds: number) => {
  const ratio = seconds / maxSeconds;

  if (ratio >= 0.5) return 'status.started';
  if (ratio < 0.5 && ratio >= 0.2) return 'orange.contrast';
  return 'red.contrast';
};

const getQuestionStatusLabel = (maxSeconds: number, seconds: number) => {
  const ratio = seconds / maxSeconds;

  if (ratio >= 0.5) return 'зеленый';
  if (ratio < 0.5 && ratio >= 0.2) return 'оранжевый';
  return 'красный';
};

export interface IAnswerForm {
  answer: string | undefined;
  voiceRecord: Blob;
  partId: string | undefined;
  parts: { id: string; content: string; checked: boolean }[] | undefined;
}

interface QuestionPageProps {
  refetchTrialInfo: RefetchTrialInfo;
  remainingTime: number | null;
}

const QuestionPage = ({ refetchTrialInfo, remainingTime }: QuestionPageProps) => {
  const navigate = useNavigate();
  const {
    handlePreventSelectStart,
    handlePreventCopy,
    handlePreventContextMenu,
    preventCopyStyles,
  } = usePreventCopy();

  // TODO: вернуть, когда понадобится
  // const { preventPasteStyles } = usePreventPaste();

  const { showResultModal } = useResultModal();
  const { t } = useTranslation();
  const [
    currentQuestion,
    questionIndex,
    assessmentInfo,
    questionTimer,
    questions,
    insertCount,
    insertSymbols,
  ] = useUnit([
    $currentQuestion,
    $questionIndex,
    $assessmentInfo,
    $questionTimer,
    $questions,
    $insertCount,
    $insertSymbols,
  ]);
  const [isDelayLoading, setIsDelayLoading] = useState(false);
  const [isSkipLoading, setIsSkipLoading] = useState(false);
  const [isSaveAnswerLoading, setIsSaveAnswerLoading] = useState(false);
  const { showDialog, closeDialog } = useDialog();
  const { showErrorToast, showSuccessToast } = useToast();

  const handleFinishQuestion = () => {
    resetQuestionTimer();
    resetAnswerMetadata();
    return refetchTrialInfo();
  };

  const voiceRecorded = useVoiceRecorded();

  useEffect(() => {
    startQuestionTimer();
  }, []);

  const [saveAnswer, { loading: saveAnswerLoading }] = useSaveAnswerMutation();
  const [delayQuestion, { loading: delayQuestionLoading }] = useDelayQuestionMutation();
  const [skipQuestion, { loading: skipQuestionLoading }] = useSkipQuestionMutation();

  const form = useForm<IAnswerForm>({
    defaultValues: {
      answer: '',
      partId: undefined,
      parts: currentQuestion?.parts?.map(({ id: partId, content }) => ({
        id: partId,
        content,
        checked: false,
      })),
    },
  });
  const { handleSubmit, trigger } = form;

  const isMultiChoiceQuestion = currentQuestion?.type === Question_Type.MultipleChoiceQuestions;
  const isLastQuestion = questionIndex === questions.length - 1;
  const questionOrder = questions[questionIndex].order + 1;

  const handleFinishTesting = () => {
    resetQuestionTimer();
    setAssessmentStatus(Trial_Status.Completed);
    navigate(ROUTES.ASSESSMENT, { replace: true, state: Trial_Status.Completed });
    showResultModal({
      specialization: assessmentInfo?.examination.competencyMap.specialization,
      onClose: () => resetAssessment(),
    });
  };

  const onConfirm = async (formData: IAnswerForm) => {
    if (currentQuestion && assessmentInfo) {
      let mp3File;

      if (formData.voiceRecord) {
        const mp3Blob = formData.voiceRecord;
        mp3File = new File([mp3Blob], 'audio.mp3', { type: 'audio/mpeg' });
      }

      const input: SaveAnswerInput = {
        audioFile: mp3File,
        answer: !mp3File ? formData.answer : undefined,
        time: questionTimer,
        questionId: currentQuestion.id,
        trialId: assessmentInfo.id,
        metadata: {
          insertCount,
          insertSymbols,
        },
      };

      console.log('insertCount', insertCount);
      console.log('insertSymbols', insertSymbols);

      if (isMultiChoiceQuestion) {
        input.partsIds = formData.partId
          ? [formData.partId]
          : formData.parts?.filter((part) => part.checked).map((part) => part.id);
      }

      setIsSaveAnswerLoading(true);
      saveAnswer({
        variables: {
          input,
        },
      })
        .then((res) => {
          if (res.data?.saveAnswer) {
            showSuccessToast(t('successMessage'));

            if (isLastQuestion) {
              handleFinishTesting();
            } else {
              handleFinishQuestion().finally(() => {
                setIsSaveAnswerLoading(false);
              });
            }
          } else {
            showErrorToast(t('errors.common'));
          }
        })
        .catch(() => {
          setIsSaveAnswerLoading(false);
          showErrorToast(t('errors.common'));
        });
    } else {
      showErrorToast(t('errors.common'));
    }
  };

  const onSaveAnswer = (formData: IAnswerForm) => {
    const isRecordingInProgress = voiceRecorded.status === RecordStatus.Recording;

    if (isRecordingInProgress) {
      showDialog({
        title: t('assessment.question.recordingInProgress.title'),
        content: t('assessment.question.recordingInProgress.message'),
        cancelText: t('assessment.question.recordingInProgress.confirm'),
      });
    } else {
      showDialog({
        title: t('assessment.confirmWarning.title'),
        content: t('assessment.confirmWarning.description'),
        confirmText: t('confirm'),
        cancelText: t('cancel'),
        onConfirm: async () => {
          closeDialog();
          await onConfirm(formData);
        },
      });
    }
  };

  const isAudioConverting = voiceRecorded.status === RecordStatus.Converting;

  const updateInsertParams = (clipboardData: DataTransfer) => {
    const insertedText = clipboardData.getData('text/html');
    setInsertCount(insertCount + 1);
    setInsertSymbols(insertSymbols + insertedText.length);
  };

  const onPasteAnswer = (e: ClipboardEvent<HTMLDivElement>) => {
    updateInsertParams(e.clipboardData);
  };

  const handleReady = (editor: ClassicEditor) => {
    editor.editing.view.document.on('paste', (_, data) => {
      updateInsertParams(data.dataTransfer);
    });
  };

  const getAnswerComponent = () => {
    const isTextAnswerRequired = !voiceRecorded.audioSrc ? 'Поле обязательно' : false;

    if (currentQuestion?.type === Question_Type.Practical) {
      return (
        <TextEditorControl
          name='answer'
          // @ts-expect-error пропсы из Props<ClassicEditor> полноценно не подтягиваются из @myhrbox/myhrbox-frontend-core из-за неправильного импорта их из @ckeditor/ckeditor5-react, но в сам компонент в итоге прокидываются
          onReady={handleReady}
          rules={{ required: isTextAnswerRequired }}
        />
      );
    }

    if (isMultiChoiceQuestion) {
      if (currentQuestion.correctCountType === Question_Correct_Count_Type.SingleCorrect) {
        return <SingleChoiceAnswer currentQuestion={currentQuestion} />;
      }

      return <MultiChoiceAnswer form={form} />;
    }

    return (
      <TextFieldControl
        name='answer'
        multiline
        minRows={5}
        onPaste={onPasteAnswer}
        rules={{ required: isTextAnswerRequired }}
        // TODO: вернуть, когда понадобится
        // sx={preventPasteStyles}
        // onContextMenu={(e) => e.preventDefault()}
      />
    );
  };

  const handleDelayQuestion = () => {
    showDialog({
      title: t('assessment.question.delayQuestionConfirm.title'),
      content: t('assessment.question.delayQuestionConfirm.content'),
      cancelText: t('cancel'),
      confirmText: t('assessment.question.delayQuestionConfirm.confirm'),
      onConfirm: async () => {
        setIsDelayLoading(true);
        closeDialog();
        delayQuestion({
          variables: {
            input: {
              questionId: questions[questionIndex].questionId,
              trialId: assessmentInfo!.id,
              candidateTime: questionTimer,
            },
          },
        })
          .then(() => {
            handleFinishQuestion().finally(() => {
              setIsDelayLoading(false);
            });
          })
          .catch(() => {
            setIsDelayLoading(false);
            showErrorToast(t('errors.common'));
          });
      },
    });
  };

  const handleSkipQuestion = () => {
    showDialog({
      title: t('assessment.question.skipQuestionConfirm.title'),
      content: t('assessment.question.skipQuestionConfirm.content'),
      cancelText: t('cancel'),
      confirmText: t('assessment.question.skipQuestionConfirm.confirm'),
      onConfirm: async () => {
        setIsSkipLoading(true);
        closeDialog();
        skipQuestion({
          variables: {
            input: {
              questionId: questions[questionIndex].questionId,
              trialId: assessmentInfo!.id,
            },
          },
        })
          .then(() => {
            if (isLastQuestion) {
              handleFinishTesting();
            } else {
              handleFinishQuestion().finally(() => {
                setIsSkipLoading(false);
              });
            }
          })
          .catch(() => {
            setIsSkipLoading(false);
            showErrorToast(t('errors.common'));
          });
      },
    });
  };

  useEffect(() => {
    if (voiceRecorded.status === RecordStatus.Done && voiceRecorded.audioSrc) {
      trigger();
    }
  }, [voiceRecorded]);

  const isQuestionDelayed = questions[questionIndex].status === Trial_Question_Status.Delayed;

  return (
    <Box>
      <Typography variant='h28' mb={1.5}>
        {t('assessment.question.title', { order: questionOrder })}
      </Typography>
      <TextFormatter
        text={currentQuestion?.text || ''}
        variant='body16rg'
        mb={3.8}
        sx={preventCopyStyles}
        onSelectCapture={handlePreventSelectStart}
        onCopy={handlePreventCopy}
        onContextMenu={handlePreventContextMenu}
      />
      <Stack direction='row' justifyContent='space-between' alignItems='center'>
        <Typography variant='h28' mb={1.5}>
          {t('assessment.question.answerTitle')}
        </Typography>
        <Stack direction='row' spacing={1.5}>
          {currentQuestion && currentQuestion.time && (
            <Timer
              seconds={
                currentQuestion.time - (questions[questionIndex].candidateTime || 0) - questionTimer
              }
              color={(seconds) => getQuestionTimerColor(currentQuestion.time, seconds)}
              ariaLabel={(seconds) =>
                `Оставшееся время на вопрос: ${secondsToTimeStringFormat(seconds, t)}, статус: ${getQuestionStatusLabel(currentQuestion.time, seconds)}`
              }
              hideTimerOnExpire
              pulseOnEnding
              tooltipTitle='Обратный отсчёт на этот вопрос. По нулю вопрос можно продолжать, таймер скрывается.'
            />
          )}
          {assessmentInfo && remainingTime && (
            <Timer
              seconds={remainingTime - questionTimer}
              color='status.received'
              ariaLabel={(seconds) =>
                `Оставшееся время теста: ${secondsToTimeStringFormat(seconds, t)}`
              }
              hideTimerOnExpire
              sx={{ width: 120, textAlign: 'center' }}
              tooltipTitle='Обратный отсчёт всего теста. По нулю тест продолжается, таймер скрывается.'
            />
          )}
        </Stack>
      </Stack>
      <Form form={form}>
        <Box mb={2}>{getAnswerComponent()}</Box>
        <Grid2 container spacing={2}>
          {!isMultiChoiceQuestion && (
            <Grid2 size={{ xs: 12, md: 6 }}>
              <VoiceRecordButton voiceRecorded={voiceRecorded} />
            </Grid2>
          )}
          <Grid2 size={{ xs: 12, md: 6 }}>
            <Button
              fullWidth
              size='large'
              disabled={
                isAudioConverting ||
                skipQuestionLoading ||
                isSkipLoading ||
                delayQuestionLoading ||
                isDelayLoading
              }
              loading={saveAnswerLoading || isSaveAnswerLoading}
              variant='contained'
              onClick={handleSubmit(onSaveAnswer)}>
              {t('assessment.question.submitAnswer')}
            </Button>
          </Grid2>
        </Grid2>
      </Form>
      <Instruction />
      <Grid2 container spacing={2} sx={{ mt: 2 }}>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <Button
            fullWidth
            size='large'
            disabled={
              delayQuestionLoading || isDelayLoading || saveAnswerLoading || isSaveAnswerLoading
            }
            loading={skipQuestionLoading || isSkipLoading}
            variant='outlined'
            color='warning'
            onClick={handleSkipQuestion}>
            {t('assessment.question.skipQuestion')}
          </Button>
        </Grid2>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <Tooltip
            title={isQuestionDelayed ? t('assessment.question.doubleDelayQuestionDisabled') : ''}>
            <span>
              <Button
                fullWidth
                size='large'
                disabled={
                  isLastQuestion ||
                  isQuestionDelayed ||
                  skipQuestionLoading ||
                  isSkipLoading ||
                  saveAnswerLoading ||
                  isSaveAnswerLoading
                }
                loading={delayQuestionLoading || isDelayLoading}
                variant='outlined'
                onClick={handleDelayQuestion}>
                {t('assessment.question.delayQuestion')}
              </Button>
            </span>
          </Tooltip>
        </Grid2>
      </Grid2>
    </Box>
  );
};

export default QuestionPage;
